import { useCallback, useEffect } from 'react';

export const PendoEvents = {
    COPY: {
        action: 'Copy action',
        eventNames: ['copy', 'cut'],
    },
    PASTE: {
        action: 'Paste action',
        eventNames: ['paste'],
    },
} as const;

type PendoEventsType = typeof PendoEvents[keyof typeof PendoEvents];

export type PendoEvent = {
    event: PendoEventsType;
    metadata?: Record<string, any>;
    eventHandler?: () => void;
};

interface UsePendoEventTrackerOptions {
    eventsToTrack: PendoEvent[];
}

/**
 * Hook to track Pendo events
 * @returns A function to track Pendo events
 */
export const usePendoEventTracker = (ref: React.RefObject<HTMLElement>, options: UsePendoEventTrackerOptions) => {
    const { eventsToTrack } = options;

    /**
     * Tracks a Pendo event
     * @param pendoEvent - The event to track
     * @param metadata - Optional metadata to include with the event
     */
    const trackEvent = useCallback(
        (pendoEvent: PendoEventsType, metadata?: Record<string, any>, eventHandler?: () => void) => {
            if (window.pendo && typeof window.pendo.track === 'function') {
                window.pendo.track(pendoEvent.action, {
                    ...metadata,
                    timestamp: new Date().toISOString(),
                });

                if (eventHandler) {
                    eventHandler();
                }
            } else {
                console.debug('Pendo tracking not available', { pendoEvent, metadata });
            }
        },
        [],
    );

    useEffect(() => {
        const element = ref.current;

        if (!element || !eventsToTrack.length) return undefined;

        const eventHandlers = eventsToTrack.map(({ event, metadata, eventHandler }) => {
            const handler: EventListener = (e: Event) => {
                if (e.type === 'paste' && e instanceof ClipboardEvent) {
                    return trackEvent(
                        event,
                        { ...metadata, text: e.clipboardData!.getData('text/plain') },
                        eventHandler,
                    );
                }
                return trackEvent(event, metadata, eventHandler);
            };

            event.eventNames.forEach((eventName: string) => {
                element.addEventListener(eventName, handler);
            });
            return { event, handler };
        });

        return () => {
            eventHandlers.forEach(({ event, handler }) => {
                event.eventNames.forEach((eventName: string) => {
                    element.removeEventListener(eventName, handler);
                });
            });
        };
    }, [ref, eventsToTrack, trackEvent]);
};
